import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Get directory base URL
const getDirectoryBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }
    return 'https://directory.visibeen.com';
  }
  return 'https://directory.visibeen.com';
};

// Get API base URL
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
    return 'https://api.visibeen.com';
  }
  return 'https://api.visibeen.com';
};

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: '#faf9f7',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-100px',
    right: '-100px',
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(201, 162, 39, 0.06) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(201, 162, 39, 0.1)',
  border: '1px solid rgba(201, 162, 39, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#2db9b0',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: 'clamp(32px, 5vw, 48px)',
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #2db9b0 0%, #a67c52 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const ListingsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '28px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const ListingCard = styled('a')(({ index }) => ({
  display: 'flex',
  flexDirection: 'column',
  background: '#ffffff',
  borderRadius: '24px',
  overflow: 'hidden',
  textDecoration: 'none',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    '& .listing-title': {
      color: '#2db9b0'
    },
    '& .card-image img': {
      transform: 'scale(1.08)'
    }
  }
}));

const ListingImage = styled(Box)({
  width: '100%',
  height: '180px',
  background: 'linear-gradient(135deg, #1b1f3a 0%, #23284d 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '250px',
    height: '250px',
    background: 'radial-gradient(circle, rgba(54, 214, 208, 0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0
  }
});

const PlaceholderIcon = styled(Box)({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  background: 'rgba(54, 214, 208, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#36d6d0',
  fontSize: '32px',
  fontFamily: "'Cormorant Garamond', serif",
  fontWeight: 600,
  position: 'relative',
  zIndex: 1
});

const ListingContent = styled(Box)({
  padding: '24px'
});

const ListingCategory = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 14px',
  background: 'rgba(201, 162, 39, 0.1)',
  borderRadius: '50px',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '11px',
  fontWeight: 600,
  color: '#2db9b0',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '14px'
});

const ListingTitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '18px',
  fontWeight: 600,
  color: '#1a1a1a',
  marginBottom: '10px',
  transition: 'color 0.3s ease',
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const ListingAddress = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  color: '#666666',
  lineHeight: 1.5,
  '& svg': {
    fontSize: '16px',
    color: '#2db9b0',
    marginTop: '2px',
    flexShrink: 0
  }
});

const ListingMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginTop: '16px',
  paddingTop: '16px',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)'
});

const RatingBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#1a1a1a',
  '& svg': {
    fontSize: '16px',
    color: '#36d6d0'
  }
});

const LocationBadge = styled(Box)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: '#888888'
});

const LoadingWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '60px'
});

const ViewAllWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '48px',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: '0.4s',
  animationFillMode: 'backwards'
});

const ViewAllButton = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  padding: '18px 40px',
  background: 'transparent',
  border: '2px solid rgba(201, 162, 39, 0.5)',
  borderRadius: '60px',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2db9b0 0%, #e8d5a3 100%)',
    borderColor: '#2db9b0',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(201, 162, 39, 0.25)',
    '& .btn-text': {
      color: '#1a1a1a'
    },
    '& .btn-icon': {
      color: '#1a1a1a',
      transform: 'translateX(4px)'
    }
  }
});

const ViewAllText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '15px',
  fontWeight: 600,
  color: '#2db9b0',
  transition: 'color 0.3s ease'
});

const ViewAllIcon = styled(ArrowForwardIcon)({
  fontSize: '20px',
  color: '#2db9b0',
  transition: 'all 0.3s ease'
});

const RelatedServicesSection = ({ businessInfo, contactInfo, categories, currentSlug }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationInfo, setLocationInfo] = useState({ city: null, state: null, category: null });

  useEffect(() => {
    const fetchRelatedListings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Extract category from businessInfo or categories prop
        let category = null;
        if (Array.isArray(categories) && categories.length > 0) {
          category = typeof categories[0] === 'string' ? categories[0] : categories[0]?.displayName || categories[0]?.name;
        }
        if (!category && businessInfo?.category) {
          category = businessInfo.category;
        }

        // Extract location from contactInfo
        let city = null;
        let state = null;

        if (contactInfo?.address) {
          // Try to parse city and state from address string
          const addressParts = contactInfo.address.split(',').map(p => p.trim());
          if (addressParts.length >= 2) {
            city = addressParts[addressParts.length - 3] || addressParts[0];
            state = addressParts[addressParts.length - 2];
          }
        }

        if (!category) {
          setLoading(false);
          return;
        }

        setLocationInfo({ city, state, category });

        // Build query params
        const params = new URLSearchParams();
        params.append('category', category);
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (currentSlug) params.append('excludeSlug', currentSlug);
        // Also exclude by business name to ensure current business is not shown
        if (businessInfo?.name) params.append('excludeTitle', businessInfo.name);
        params.append('limit', '6');

        const apiUrl = `${getApiBaseUrl()}/api/v1/directory/related?${params.toString()}`;
        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch related services');
        }

        const data = await response.json();

        if (data.success && data.listings) {
          setListings(data.listings);
          if (data.city) setLocationInfo(prev => ({ ...prev, city: data.city }));
          if (data.state) setLocationInfo(prev => ({ ...prev, state: data.state }));
        }
      } catch (err) {
        console.error('[RelatedServicesSection] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedListings();
  }, [businessInfo, contactInfo, categories, currentSlug]);

  // Don't render if no category available
  if (!locationInfo.category && !loading) {
    return null;
  }

  // Don't render if no listings found (after loading)
  if (!loading && listings.length === 0) {
    return null;
  }

  const directoryBaseUrl = getDirectoryBaseUrl();

  // Build view all URL - use state level
  const getViewAllUrl = () => {
    if (locationInfo.state && locationInfo.category) {
      const stateSlug = locationInfo.state.toLowerCase().replace(/\s+/g, '-');
      // Link to state page with category filter
      return `${directoryBaseUrl}/in/${stateSlug}`;
    }
    return directoryBaseUrl;
  };

  return (
    <SectionContainer id="related-services">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <BusinessIcon sx={{ fontSize: 16, color: '#2db9b0' }} />
            <BadgeText>Discover More</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Related Services in <GradientText>Cities</GradientText>
          </SectionTitle>
        </SectionHeader>

        {loading ? (
          <LoadingWrapper>
            <CircularProgress size={40} sx={{ color: '#2db9b0' }} />
          </LoadingWrapper>
        ) : error ? (
          <Typography sx={{ textAlign: 'center', color: '#888', fontFamily: "'Space Grotesk', sans-serif" }}>
            Unable to load related services
          </Typography>
        ) : (
          <>
            <ListingsGrid>
              {listings.map((listing, index) => (
                <ListingCard
                  key={listing.id}
                  index={index}
                  href={`${directoryBaseUrl}${listing.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ListingImage className="card-image">
                    {listing.logo ? (
                      <img src={listing.logo} alt={listing.title} />
                    ) : (
                      <PlaceholderIcon>
                        {listing.title?.charAt(0)?.toUpperCase() || 'B'}
                      </PlaceholderIcon>
                    )}
                  </ListingImage>
                  <ListingContent>
                    <ListingCategory>{listing.category || 'Business'}</ListingCategory>
                    <ListingTitle className="listing-title">{listing.title}</ListingTitle>
                    {listing.address && (
                      <ListingAddress>
                        <LocationOnIcon />
                        <span>{listing.address}</span>
                      </ListingAddress>
                    )}
                    <ListingMeta>
                      {listing.rating && (
                        <RatingBadge>
                          <StarIcon />
                          {listing.rating}
                        </RatingBadge>
                      )}
                      {listing.city && (
                        <LocationBadge>{listing.city}</LocationBadge>
                      )}
                    </ListingMeta>
                  </ListingContent>
                </ListingCard>
              ))}
            </ListingsGrid>

            <ViewAllWrapper>
              <ViewAllButton
                href={getViewAllUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ViewAllText className="btn-text">View All Services</ViewAllText>
                <ViewAllIcon className="btn-icon" />
              </ViewAllButton>
            </ViewAllWrapper>
          </>
        )}
      </ContentWrapper>
    </SectionContainer>
  );
};

export default RelatedServicesSection;



